import { useEffect, useState } from 'react'
import { supabase } from '@/supabase/client'
import { useStorybookContext, getWeekRange } from './useStorybookContext'

interface StorybookHistoryEntry {
  id: string
  title: string | null
  created_at: string | null
  status: string | null
}

export default function StorybooksSettings() {
  const {
    enabled,
    loading,
    error,
    classroom,
    featureEnabled,
    creditsRemaining,
    refresh,
    teacherId,
    storybooksThisWeek,
    hasFreeAvailable,
    nextStorybookCost,
    freePerWeek,
    storybookCost,
  } = useStorybookContext()
  const [history, setHistory] = useState<StorybookHistoryEntry[]>([])
  const [historyError, setHistoryError] = useState<Error | null>(null)

  useEffect(() => {
    if (!teacherId) return
    let active = true

    const loadHistory = async () => {
      const { data, error: historyErr } = await supabase
        .from('story_books')
        .select('id, title, created_at, status')
        .eq('teacher_id', teacherId)
        .order('created_at', { ascending: false })
        .limit(10)

      if (!active) return

      if (historyErr && historyErr.code !== 'PGRST116') {
        setHistoryError(new Error(historyErr.message))
      } else {
        setHistory((data as StorybookHistoryEntry[]) ?? [])
      }
    }

    loadHistory()
    return () => {
      active = false
    }
  }, [teacherId])

  if (!enabled) {
    return <p className="text-sm text-cocoa/70">Storybooks are not enabled for this deployment.</p>
  }

  if (loading) {
    return <p className="text-sm text-cocoa/70">Loading Storybooks settings…</p>
  }

  if (error) {
    return (
      <p className="rounded-2xl bg-rose-100 p-4 text-sm text-rose-700">
        We couldn’t load Storybooks settings right now. Please refresh and try again later.
      </p>
    )
  }

  if (!classroom) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold text-cocoa">Storybooks settings</h2>
        <p className="rounded-2xl bg-shell p-4 text-sm text-cocoa/70">
          Once your classroom is registered, you’ll see Storybooks credits and feature toggles here.
        </p>
      </div>
    )
  }

  const { start, end } = getWeekRange()

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <p className="text-sm uppercase tracking-wide text-cocoa/60">Settings</p>
        <h1 className="text-3xl font-semibold text-cocoa">Storybooks access & credits</h1>
        <p className="text-cocoa/70">Track your weekly credits and classroom eligibility for the Storybooks feature.</p>
      </header>

      <section className="rounded-3xl border border-sand bg-white p-5 shadow-soft">
        <h2 className="text-lg font-semibold text-cocoa">Classroom access</h2>
        <dl className="mt-4 grid gap-4 text-sm text-cocoa/70 md:grid-cols-2">
          <div>
            <dt className="font-medium text-cocoa/80">Classroom</dt>
            <dd>{classroom.name || 'Unnamed classroom'}</dd>
          </div>
          <div>
            <dt className="font-medium text-cocoa/80">Storybooks enabled</dt>
            <dd>{featureEnabled ? 'Yes — you can create storybooks.' : 'Not yet — reach out to enable this feature.'}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-cocoa/60">
          Feature toggles are managed by the Project Tower team. Contact support if you’d like to opt in or out.
        </p>
      </section>

      <section className="rounded-3xl border border-sand bg-white p-5 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-cocoa">Storybook credits</h2>
            <p className="text-sm text-cocoa/70">Current period: {start} – {end}</p>
          </div>
          <button type="button" className="btn-secondary" onClick={refresh}>
            Refresh
          </button>
        </div>
        <dl className="mt-4 grid gap-4 text-sm text-cocoa/70 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="font-medium text-cocoa/80">Tower Credits balance</dt>
            <dd className="text-lg font-semibold text-cocoa">{creditsRemaining} TC</dd>
          </div>
          <div>
            <dt className="font-medium text-cocoa/80">Free this week</dt>
            <dd className="text-lg font-semibold text-cocoa">
              {hasFreeAvailable ? (
                <span className="text-emerald-600">1 available</span>
              ) : (
                <span className="text-cocoa/60">{storybooksThisWeek}/{freePerWeek} used</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-cocoa/80">Storybooks this week</dt>
            <dd className="text-lg font-semibold text-cocoa">{storybooksThisWeek}</dd>
          </div>
          <div>
            <dt className="font-medium text-cocoa/80">Next storybook cost</dt>
            <dd className="text-lg font-semibold text-cocoa">
              {nextStorybookCost === 0 ? (
                <span className="text-emerald-600">Free</span>
              ) : (
                <span>{nextStorybookCost} TC</span>
              )}
            </dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-cocoa/60">
          You get {freePerWeek} free storybook per week. Additional storybooks cost {storybookCost} Tower Credits each.
        </p>
      </section>

      <section className="rounded-3xl border border-sand bg-white p-5 shadow-soft">
        <h2 className="text-lg font-semibold text-cocoa">Recent storybooks</h2>
        {historyError && (
          <p className="mt-2 rounded-2xl bg-rose-100 p-3 text-xs text-rose-700">Unable to load storybook history right now.</p>
        )}
        {history.length === 0 ? (
          <p className="mt-3 text-sm text-cocoa/70">No storybooks created yet. Start your first one from the Storybooks page.</p>
        ) : (
          <div className="mt-3 overflow-hidden rounded-2xl border border-sand">
            <table className="w-full text-left text-sm text-cocoa/70">
              <thead className="bg-shell/60 text-xs uppercase tracking-wide text-cocoa/60">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => {
                  const createdAt = entry.created_at
                    ? new Date(entry.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : '—'
                  return (
                    <tr key={entry.id} className="border-t border-sand/60">
                      <td className="px-4 py-3 font-medium text-cocoa">{entry.title || 'Untitled'}</td>
                      <td className="px-4 py-3">{createdAt}</td>
                      <td className="px-4 py-3 capitalize">{entry.status || 'draft'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
